let __API_BASE = null;

async function tryBase(base) {
  if (!base) return null;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 1500);
    const r = await fetch(`${base}/api/health`, { signal: ctrl.signal });
    clearTimeout(t);
    return r.ok && (r.headers.get("content-type") || "").includes("application/json") ? base : null;
  } catch { return null; }
}

export async function getApiBase() {
  if (__API_BASE) return __API_BASE;

  const envBase = import.meta?.env?.VITE_API_BASE && String(import.meta.env.VITE_API_BASE).trim();
  const isPages = /\.pages\.dev$/.test(window.location.hostname);

  // 1) Prefer the configured Cloud Run base when present
  if (envBase) {
    __API_BASE = envBase.replace(/^http:/, "https:");
    console.log("[api] base:", __API_BASE, "(from VITE_API_BASE)");
    return __API_BASE;
  }

  // 2) Fallback: if we're on a Cloudflare Pages preview and no env var is present,
  //    use the known Cloud Run URL (so previews never silently break).
  if (isPages) {
    __API_BASE = "https://ethira-api-765976836622.europe-west2.run.app";
    console.log("[api] base:", __API_BASE, "(pages.dev fallback)");
    return __API_BASE;
  }

  // 3) Last resort: same-origin (for local dev / future proxy)
  __API_BASE = window.location.origin.replace(/^http:/, "https:");
  console.log("[api] base:", __API_BASE, "(same-origin fallback)");
  return __API_BASE;
}






export async function buildApiUrl(path) {
  const base = await getApiBase();
  return `${base}${path}`;
}

export async function getJSON(path) {
  const url = await buildApiUrl(path);
  const r = await fetch(url, { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error(await r.text().catch(() => `GET ${path} failed (${r.status})`));
  return r.json();
}

export async function postJSON(path, body) {
  const url = await buildApiUrl(path);
  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await r.text().catch(() => `POST ${path} failed (${r.status})`));
  return r.json();
}

export const apiPOST = postJSON;
export const apiGET  = getJSON;

/** Ensure any http:// URL is forced to https, and turn relative /api paths absolute */
export async function normalizeApiUrl(urlOrPath) {
  if (!urlOrPath) return "";
  const fixed = urlOrPath.replace(/^http:/, "https:");
  try { new URL(fixed); return fixed; } catch { /* relative */ }
  return buildApiUrl(fixed);
}

// Try multiple endpoints for logging a guide download.
// You can override the first one via VITE_DOWNLOAD_ENDPOINT.
export async function postGuideDownload(payload) {
  const configured = import.meta?.env?.VITE_DOWNLOAD_ENDPOINT && String(import.meta.env.VITE_DOWNLOAD_ENDPOINT).trim();
  const candidates = [
    configured,                       // e.g. "/api/analytics/guide-download"
    "/api/sheets/guide-download",     // legacy path
    "/api/guides/download",           // common alternative
    "/api/downloads/guide",           // another common alternative
    "/api/sheets/download"            // fallback
  ].filter(Boolean);

  let lastErr;
  for (const path of candidates) {
    try {
      const json = await postJSON(path, payload);
      // Accept either a success flag or a returned URL.
      if (json && (json.ok || json.downloadUrl)) return json;
      // If server returns something else but 200, still return it.
      return json;
    } catch (e) {
      const msg = String(e?.message || "");
      // If it's a 404 / "Cannot POST", keep trying the next candidate.
      if (/404|cannot post|not found/i.test(msg)) {
        lastErr = e;
        continue;
      }
      // Other errors (401, 500, CORS) → stop and surface.
      throw e;
    }
  }
  throw lastErr || new Error("All guide-download endpoints failed");
}

